import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from "../AuthContext.jsx";
import { PROVIDER_TYPES, PROVIDER_CATEGORIES } from "../utils/providerTypeHelpers";
import { isPlatformAdminUser } from '../utils/platformAccess';
import { restrictedHome } from '../utils/accountKind';

import { safeStorage } from '../utils/safeStorage';
export default function ProtectedRoute({ 
  children, 
  requiredPermission, 
  requiredRole,
  requiredProviderType,      // NEW: Single provider type or array
  requiredProviderCategory,  // NEW: Single category or array
  allowSuperAdmin = true,    // NEW: Whether super_admin bypasses provider checks
  platformOnly = false       // Require a tenant-less platform super_admin
}) {
  const token = safeStorage.getItem("evv_token");
  const { user, loading } = useAuth();
  const location = useLocation();

  // If no token, redirect to login page instead of home
  if (!token) return <Navigate to="/login" replace />;
  
  // Show loading while authentication is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user after loading, redirect to login
  if (!user) return <Navigate to="/login" replace />;

  // Apply this at the parent /platform route so every nested page inherits the
  // same tenant-boundary check. Requiring organizationId to be exactly null is
  // intentional: a scoped ID and a missing/legacy field are both denied.
  if (platformOnly && !isPlatformAdminUser(user)) {
    return <Navigate to="/admin" replace />;
  }

  // Confine platform administrators to the platform console.
  //
  // A platform admin has organizationId === null, so its JWT is rejected by the
  // global tenantMiddleware and EVERY tenant API call 401s. The tenant UI still
  // rendered (AdminShell picks its nav from the URL, and canSee() short-circuits
  // to true for super_admin), producing a WeCare-looking sidebar where nothing
  // loaded. Cross-tenant tools live under /platform instead.
  //
  // Tested explicitly against `=== null`, NOT falsiness: ola@wecaremn.org is a
  // super_admin WITH an organizationId and must keep full access to /admin. A
  // missing field (payload bug) also must not bounce anyone.
  if (user.role === 'super_admin' && user.organizationId === null
      && !location.pathname.startsWith('/platform')) {
    return <Navigate to="/platform" replace />;
  }

  // A restricted login (a pharmacy) has one page; the API refuses the rest.
  const home = restrictedHome(user);
  if (home && !location.pathname.startsWith(home)) {
    return <Navigate to={home} replace />;
  }

  // Check role requirements
  if (requiredRole && user.role !== requiredRole && user.role !== 'super_admin') {
    // If user does not have the required role, redirect to their appropriate dashboard
    if (user.role === 'provider') return <Navigate to="/admin" replace />;
    if (user.role === 'admin') return <Navigate to="/admin" replace />;
    if (user.role === 'custom') return <Navigate to="/admin" replace />;
    return <Navigate to="/login" replace />;
  }

  // Check permission requirements
  if (requiredPermission && user) {
    const perms = [
      ...(user.permissions || []).map(p => p.name),
      ...(user.customRole?.permissions || []).map(p => p.name),
    ];
    if (!perms.includes(requiredPermission) && user.role !== "super_admin") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
              <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">Access Denied</h2>
              <p className="text-red-600">You do not have permission to access this page.</p>
            </div>
          </div>
        </div>
      );
    }
  }

  // Check provider type requirements (NEW)
  if ((requiredProviderType || requiredProviderCategory) && user) {
    // Super admins can bypass provider type checks (unless explicitly disabled)
    if (user.role === "super_admin" && allowSuperAdmin) {
      return children;
    }

    // Helper to normalize to array
    const normalizeToArray = (value) => {
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    };

    const requiredTypes = normalizeToArray(requiredProviderType);
    const requiredCategories = normalizeToArray(requiredProviderCategory);

    // Check provider type match
    if (requiredTypes.length > 0 && user.providerType) {
      const hasRequiredType = requiredTypes.includes(user.providerType);
      if (!hasRequiredType) {
        const typeConfig = PROVIDER_TYPES[user.providerType];
        const requiredTypesLabels = requiredTypes
          .map(t => PROVIDER_TYPES[t]?.label || t)
          .join(', ');
        
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md">
                <div className="text-4xl mb-3">🚫</div>
                <h2 className="text-lg font-semibold text-yellow-800 mb-2">
                  Provider Type Required
                </h2>
                <p className="text-yellow-700 mb-3">
                  This page is only accessible to: <strong>{requiredTypesLabels}</strong>
                </p>
                <p className="text-yellow-600 text-sm">
                  Your provider type: <strong>{typeConfig?.label || user.providerType || 'Not Set'}</strong>
                </p>
                <button
                  onClick={() => window.history.back()}
                  className="mt-4 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition"
                >
                  ← Go Back
                </button>
              </div>
            </div>
          </div>
        );
      }
    }

    // Check provider category match
    if (requiredCategories.length > 0 && user.providerCategory) {
      const hasRequiredCategory = requiredCategories.includes(user.providerCategory);
      if (!hasRequiredCategory) {
        const categoryConfig = PROVIDER_CATEGORIES[user.providerCategory];
        const requiredCategoriesLabels = requiredCategories
          .map(c => PROVIDER_CATEGORIES[c]?.label || c)
          .join(', ');
        
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 max-w-md">
                <div className="text-4xl mb-3">🚫</div>
                <h2 className="text-lg font-semibold text-orange-800 dark:text-orange-300 mb-2">
                  Provider Category Required
                </h2>
                <p className="text-orange-700 dark:text-orange-300 mb-3">
                  This page is only accessible to: <strong>{requiredCategoriesLabels}</strong>
                </p>
                <p className="text-orange-600 text-sm">
                  Your provider category: <strong>{categoryConfig?.label || user.providerCategory || 'Not Set'}</strong>
                </p>
                <button
                  onClick={() => window.history.back()}
                  className="mt-4 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition"
                >
                  ← Go Back
                </button>
              </div>
            </div>
          </div>
        );
      }
    }

    // Check if provider type/category is not set but required
    if (requiredTypes.length > 0 && !user.providerType) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 max-w-md">
              <div className="text-4xl mb-3">⚠️</div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Provider Type Not Set
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Your account does not have a provider type assigned. Please contact your administrator.
              </p>
              <button
                onClick={() => window.history.back()}
                className="mt-4 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (requiredCategories.length > 0 && !user.providerCategory) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 max-w-md">
              <div className="text-4xl mb-3">⚠️</div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Provider Category Not Set
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Your account does not have a provider category assigned. Please contact your administrator.
              </p>
              <button
                onClick={() => window.history.back()}
                className="mt-4 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  return children;
}
